import { useEffect, useState } from 'react';
import { ApiError, type Api, type Readiness, type ReadinessItem } from '../api';
import { PERM, useCan } from '../access';
import type { T } from '../i18n';
import { ReadOnlyNote } from '../ui';

type Tab = 'actions' | 'voice' | 'routing' | 'knowledge';
/** Where each setting is changed on the agent page. */
const SETTING_TAB: Record<string, Tab> = { hours: 'actions', voice: 'voice', transfers: 'routing', routing_unreachable: 'routing', routing_callback: 'actions', contact_source: 'knowledge' };

/** The small summary card on the General tab: score + what is missing; opens the Readiness tab. */
export const ReadinessCard = ({ api, t, agentId, onOpen }: { api: Api; t: T; agentId: string; onOpen: () => void }) => {
  const [r, setR] = useState<Readiness>();
  useEffect(() => void api.readiness(agentId).then(setR).catch(() => undefined), [api, agentId]);
  if (!r || r.score >= 100) return null;
  const missing = r.items.filter((i) => i.status === 'missing').length;
  return (
    <button type="button" className={`card readiness-card ${r.missingHigh > 0 ? 'warn' : ''}`} onClick={onOpen}>
      <div className="row-between">
        <strong>{t('readiness.card.title', { score: r.score })}</strong>
        <span className="link">{t('readiness.card.open')} →</span>
      </div>
      <div className="hbar-track"><span className="hbar-fill" style={{ width: `${r.score}%` }} /></div>
      <span className="muted small">{r.missingHigh > 0 ? t('readiness.card.high', { n: r.missingHigh, total: missing }) : t('readiness.card.some', { n: missing })}</span>
    </button>
  );
};

/**
 * Readiness tab: the questions callers will ask and a few settings. Each shows where the agent uses it and how much
 * it matters; answers can be written with the AI in a short chat. The agent works without them; they stay listed.
 */
export const ReadinessPanel = ({ api, t, agentId, focus, onGoTab, onSaved }: { api: Api; t: T; agentId: string; focus?: string | undefined; onGoTab: (tab: Tab) => void; onSaved: () => void }) => {
  // ADR-071: answering, AI suggestions and "does not apply" need the agents permission; without it the list is read-only.
  const canEdit = useCan()(PERM.agents);
  const [r, setR] = useState<Readiness>();
  const [error, setError] = useState<string>();
  const [suggesting, setSuggesting] = useState(false);
  // Opened from statistics ("add the answer"): that question starts open and in view (open only for someone who may answer).
  const [open, setOpen] = useState<string | null>(focus ? `question:${focus}` : null);
  const load = () => api.readiness(agentId).then(setR).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  useEffect(() => void load(), [api, agentId]);
  useEffect(() => {
    if (r && focus) document.getElementById(`rq-${focus}`)?.scrollIntoView({ block: 'center' });
  }, [r, focus]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      await api.suggestReadiness(agentId);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setSuggesting(false);
    }
  };
  const dismiss = async (key: string, dismissed: boolean) => {
    await api.dismissReadiness(agentId, key, dismissed).catch(() => undefined);
    await load();
  };

  if (!r) return error ? <div className="banner error">{t('error.generic')}</div> : <p className="muted">{t('boot.loading')}</p>;
  const groups: Array<[string, ReadinessItem[]]> = [
    ['missing', r.items.filter((i) => i.status === 'missing')],
    ['done', r.items.filter((i) => i.status === 'done')],
    ['dismissed', r.items.filter((i) => i.status === 'dismissed')],
  ];
  return (
    <div className="settings readiness">
      <ReadOnlyNote t={t} need={PERM.agents} />
      <div className="card readiness-head">
        <div className="row-between">
          <div>
            <h2>{t('readiness.title', { score: r.score })}</h2>
            <p className="muted small">{t('readiness.subtitle')}</p>
          </div>
          <span className={`readiness-score ${r.score >= 80 ? 'good' : r.score >= 50 ? 'mid' : 'low'}`}>%{r.score}</span>
        </div>
        <div className="hbar-track"><span className="hbar-fill" style={{ width: `${r.score}%` }} /></div>
        <p className="hint">{t('readiness.publishNote')}</p>
        {canEdit && r.source !== 'template' && (
          <div className="readiness-suggest">
            <span className="small">{r.source === 'none' ? t('readiness.suggest.none') : t('readiness.suggest.more')}</span>
            <button type="button" className="secondary" disabled={suggesting} onClick={() => void suggest()}>{suggesting ? t('readiness.suggest.running') : `✦ ${t('readiness.suggest.button')}`}</button>
          </div>
        )}
      </div>

      {groups.map(([g, list]) => list.length > 0 && (
        <div key={g} className="readiness-group">
          <h3>{t(`readiness.group.${g}`, { n: list.length })}</h3>
          {list.map((item) => (
            <Item key={`${item.kind}:${item.id}`} api={api} t={t} agentId={agentId} item={item} canEdit={canEdit} open={canEdit && open === `${item.kind}:${item.id}`}
              onToggle={() => setOpen((o) => (o === `${item.kind}:${item.id}` ? null : `${item.kind}:${item.id}`))}
              onGoTab={onGoTab} onDismiss={dismiss} onSaved={async () => { setOpen(null); await load(); onSaved(); }} />
          ))}
        </div>
      ))}
    </div>
  );
};

const Item = ({ api, t, agentId, item, canEdit, open, onToggle, onGoTab, onDismiss, onSaved }: {
  api: Api; t: T; agentId: string; item: ReadinessItem; canEdit: boolean; open: boolean; onToggle: () => void; onGoTab: (tab: Tab) => void;
  onDismiss: (key: string, dismissed: boolean) => Promise<void>; onSaved: () => Promise<void>;
}) => {
  const key = `${item.kind === 'question' ? 'q' : 's'}:${item.id}`;
  const title = item.kind === 'question' ? item.question : t(`readiness.setting.${item.id}.title`);
  const why = item.kind === 'question' ? item.why : t(`readiness.setting.${item.id}.why`);
  return (
    <div id={item.kind === 'question' ? `rq-${item.id}` : undefined} className={`readiness-item ${item.status}`}>
      <div className="row-between">
        <div className="readiness-text">
          <strong>{item.status === 'done' ? '✓ ' : ''}{title}</strong>
          <span className={`impact ${item.impact}`}>{t(`readiness.impact.${item.impact}`)}</span>
          <p className="small muted">{why}</p>
          {item.kind === 'question' && item.answer && <p className="readiness-answer">“{item.answer}”</p>}
        </div>
        <div className="readiness-actions">
          {item.kind === 'setting' ? (
            <button type="button" className="secondary" onClick={() => onGoTab(SETTING_TAB[item.id] ?? 'knowledge')}>{t('readiness.setting.go')}</button>
          ) : canEdit && (
            <button type="button" className={item.status === 'missing' ? 'primary' : 'secondary'} onClick={onToggle}>{item.status === 'done' ? t('readiness.edit') : `✦ ${t('readiness.fill')}`}</button>
          )}
          {canEdit && item.status !== 'done' && (
            <button type="button" className="link small" onClick={() => void onDismiss(key, item.status !== 'dismissed')}>{item.status === 'dismissed' ? t('readiness.undismiss') : t('readiness.dismiss')}</button>
          )}
        </div>
      </div>
      {canEdit && open && item.kind === 'question' && <Assist api={api} t={t} agentId={agentId} item={item} onSaved={onSaved} />}
    </div>
  );
};

/** A short chat: the AI asks in plain words, drafts the answer the agent will say; the owner checks and saves it. */
const Assist = ({ api, t, agentId, item, onSaved }: { api: Api; t: T; agentId: string; item: Extract<ReadinessItem, { kind: 'question' }>; onSaved: () => Promise<void> }) => {
  const lang = document.documentElement.lang || 'en';
  const [messages, setMessages] = useState<Array<{ role: 'owner' | 'assistant'; text: string }>>([]);
  const [text, setText] = useState('');
  const [draft, setDraft] = useState(item.answer ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const ask = async (history: Array<{ role: 'owner' | 'assistant'; text: string }>) => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.assistReadiness(agentId, { questionId: item.id, messages: history, lang });
      // askOwner: nothing known yet — the panel asks in its own words (no AI call was made).
      const reply = r.askOwner ? t('readiness.assist.opening', { question: item.question }) : r.reply;
      const next = reply ? [...history, { role: 'assistant' as const, text: reply }] : history;
      setMessages(next);
      if (r.draft) setDraft(r.draft);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => void ask([]), []);

  const send = () => {
    const msg = text.trim();
    if (!msg) return;
    setText('');
    const history = [...messages, { role: 'owner' as const, text: msg }];
    setMessages(history);
    void ask(history);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.answerReadiness(agentId, item.id, draft);
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
      setBusy(false);
    }
  };

  return (
    <div className="readiness-assist">
      <div className="messages">
        {messages.map((m, i) => <div key={i} className={`bubble ${m.role === 'owner' ? 'user' : 'assistant'}`}>{m.text}</div>)}
        {busy && <div className="bubble assistant typing">{t('studio.thinking')}</div>}
      </div>
      <form className="composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea rows={2} value={text} placeholder={t('readiness.assist.placeholder')} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <button type="submit" className="primary" disabled={busy || !text.trim()}>{t('studio.send')}</button>
      </form>
      <label className="readiness-draft">{t('readiness.assist.draft')}
        <textarea rows={2} maxLength={600} value={draft} placeholder={t('readiness.assist.draftPlaceholder')} onChange={(e) => setDraft(e.target.value)} />
        <span className="hint">{t('readiness.assist.draftHint')}</span>
      </label>
      <div className="row-between">
        <span className="muted small">{error ? t('error.generic') : ''}</span>
        <button type="button" className="primary" disabled={busy || !draft.trim()} onClick={() => void save()}>{t('readiness.assist.save')}</button>
      </div>
    </div>
  );
};
